import { TextInput } from "@ucl/dse/textInput";
import { Text } from "@ucl/dse/text";
import { Icon } from "@ucl/dse/icons";
import { Control, FieldErrors, useController, useFormContext, ControllerRenderProps, FieldPath } from "react-hook-form";
import { TFunction } from "i18next";
import { autoTabHandler } from "src/utils/auto-tab";
import { parseDOBErrorStates } from "src/utils/dob-error-parser";
import React from "react";
import { FormData } from "src/pages/form-page/form-page";
import styles from "./dob-input.module.scss";

interface DOBInputProps {
    readonly t: TFunction;
    readonly control: Control<FormData>;
    readonly errors: FieldErrors<FormData>;
}

export function DOBInput({ t, control, errors }: DOBInputProps) {
    const { trigger } = useFormContext();
    const { field: dayField } = useController({ name: "dobDay", control });
    const { field: monthField } = useController({ name: "dobMonth", control });
    const { field: yearField } = useController({ name: "dobYear", control });

    const dobErrorMessage = errors.dobCombined?.message;
    const errorStates = parseDOBErrorStates(dobErrorMessage);

    const handleDayKeyUp = autoTabHandler("dobMonth", 2);
    const handleMonthKeyUp = autoTabHandler("dobYear", 2);

    const isMovingWithinDOBFields = (relatedTarget: EventTarget | null): boolean => {
        if (!relatedTarget) return false;
        const target = relatedTarget as HTMLInputElement;
        return ["dobDay", "dobMonth", "dobYear"].includes(target.name);
    };

    const padSingleDigit = (value: string): string => {
        return value.length === 1 ? `0${value}` : value;
    };

    const handleBlur = (
        e: React.FocusEvent<HTMLInputElement>,
        field: ControllerRenderProps<FormData, FieldPath<FormData>>
    ) => {
        if (e.target.name === "dobDay" || e.target.name === "dobMonth") {
            field.onChange(padSingleDigit(e.target.value));
        }
        field.onBlur();
        if (!isMovingWithinDOBFields(e.relatedTarget)) {
            trigger("dobCombined");
        }
    };

    return (
        <div className={styles.dobContainer}>
            <div className={styles.labelWrapper}>
                <Text.P size="large" data-testid="subheading" className={styles.subheading}>
                    {t("personalDetails.labels.dobCombined")}
                </Text.P>
                <span className={styles.requiredIndicator}>*</span>
            </div>
            <div className={styles.inputsWrapper}>
                <div className={styles.dayField}>
                    <TextInput
                        data-testid="dobDay"
                        autoComplete="bday-day"
                        inputMode="numeric"
                        labelProp={{
                            showRequiredIndicator: false,
                            label: t("personalDetails.labels.dobDay"),
                        }}
                        required={true}
                        placeholder=""
                        onKeyUp={handleDayKeyUp}
                        inputState={errorStates.day ? "error" : "none"}
                        type="number"
                        {...dayField}
                        onBlur={(e) => handleBlur(e, dayField)}
                    />
                </div>
                <div className={styles.monthField}>
                    <TextInput
                        data-testid="dobMonth"
                        autoComplete="bday-month"
                        inputMode="numeric"
                        labelProp={{
                            showRequiredIndicator: false,
                            label: t("personalDetails.labels.dobMonth"),
                        }}
                        type="number"
                        required={true}
                        {...monthField}
                        placeholder=""
                        onKeyUp={handleMonthKeyUp}
                        onBlur={(e) => handleBlur(e, monthField)}
                        inputState={errorStates.month ? "error" : "none"}
                    />
                </div>
                <div className={styles.yearField}>
                    <TextInput
                        data-testid="dobYear"
                        autoComplete="bday-year"
                        inputMode="numeric"
                        labelProp={{
                            showRequiredIndicator: false,
                            label: t("personalDetails.labels.dobYear"),
                        }}
                        placeholder=""
                        type="number"
                        required={true}
                        {...yearField}
                        onBlur={(e) => handleBlur(e, yearField)}
                        inputState={errorStates.year ? "error" : "none"}
                    />
                </div>
            </div>

            <div className={styles.helperWrapper}>
                <Text.P className={styles.helperText}>{t("personalDetails.dobHelperText")}</Text.P>
                {dobErrorMessage && (
                    <div className={styles.errorWrapper} data-testid="dobError">
                        <Icon icon="warningFilled" size="xxs" className={styles.errorIcon} />
                        <Text.P size="small" className={styles.errorText}>
                            {dobErrorMessage}
                        </Text.P>
                    </div>
                )}
            </div>
        </div>
    );
}
